import { useState, useMemo, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getGraphDetails, submitUnit } from "api/weeklyTracker";

const WeekGraph = ({
  graph_data,
  detailed_metrics,
  total_weeks_tracked,
  physical_trans_unit = null,
}) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("weight");
  const [selectedUnit, setSelectedUnit] = useState(
    physical_trans_unit ?? "lbs"
  );
  const [selectedWeekRange, setSelectedWeekRange] = useState("");

  // New state for dynamically fetched graph data
  const [currentGraphData, setCurrentGraphData] = useState(graph_data || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false); // Track if we've made API calls

  // Memoize the initial data setup
  useEffect(() => {
    // Only set initial data if we haven't fetched data yet for a range
    if (!currentGraphData && graph_data) {
      setCurrentGraphData(graph_data);
    }
  }, [graph_data, currentGraphData]);

  // Generate week ranges based on total_weeks_tracked
  const weekRanges = useMemo(() => {
    if (!total_weeks_tracked || total_weeks_tracked === 0) return [];

    const ranges = [];
    const totalWeeks = parseInt(total_weeks_tracked);

    // Calculate how many full 5-week ranges we have
    const fullRanges = Math.floor(totalWeeks / 5);
    const remainingWeeks = totalWeeks % 5;

    // Create full 5-week ranges
    for (let i = 0; i < fullRanges; i++) {
      const start = i * 5 + 1;
      const end = (i + 1) * 5;
      ranges.push({
        label: `Week ${start} - ${end}`,
        value: `${start}-${end}`,
        start,
        end,
      });
    }

    // Add remaining weeks if any
    if (remainingWeeks > 0) {
      const start = fullRanges * 5 + 1;
      const end = totalWeeks;
      if (start === end) {
        ranges.push({
          label: `Week ${start}`,
          value: `${start}-${end}`,
          start,
          end,
        });
      } else {
        ranges.push({
          label: `Week ${start} - ${end}`,
          value: `${start}-${end}`,
          start,
          end,
        });
      }
    }

    return ranges;
  }, [total_weeks_tracked]);

  // Set default to latest week range on component mount - ONLY ONCE
  useEffect(() => {
    if (weekRanges.length > 0 && !selectedWeekRange) {
      const latestRange = weekRanges[weekRanges.length - 1].value;
      setSelectedWeekRange(latestRange);
    }
  }, [weekRanges]); // Remove selectedWeekRange from dependencies

  // API call function
  const fetchGraphData = useCallback(async (weekRange) => {
    if (!weekRange) return;

    setIsLoading(true);
    try {
      const response = await getGraphDetails(weekRange);
      setCurrentGraphData(response.graph_data);
      setHasFetchedData(true); // Mark that we've made API calls
    } catch (error) {
      console.error("Error fetching graph details:", error);
      setCurrentGraphData(null); // Clear data on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUnit = async (unit) => {
    setIsLoading(true);
    try {
      await submitUnit(unit);
      setSelectedUnit(unit);
    } catch (error) {
      console.error("Error in submitting unit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch data ONLY when selectedWeekRange changes AND it's not the initial load
  useEffect(() => {
    if (!selectedWeekRange) return;

    const rangeObject = weekRanges.find((r) => r.value === selectedWeekRange);
    if (rangeObject && hasFetchedData) {
      // Only fetch if we've already made at least one user-initiated change
      fetchGraphData(rangeObject.value);
    }
  }, [selectedWeekRange, fetchGraphData, weekRanges, hasFetchedData]);

  // Handle week range change
  const handleWeekRangeChange = (e) => {
    const newRange = e.target.value;
    setSelectedWeekRange(newRange);
    // Set flag to indicate we should fetch data for future changes
    if (!hasFetchedData) {
      setHasFetchedData(true);
    }
  };

  // Process graph data with fallback
  const processedData = useMemo(() => {
    if (!currentGraphData) {
      return {
        weightProgress: [],
        waistProgress: [],
        bodyFatProgress: [],
        wellnessProgress: [],
      };
    }

    return {
      weightProgress: currentGraphData.weight_progress || [],
      waistProgress: currentGraphData.waist_progress || [],
      bodyFatProgress: currentGraphData.body_fat_progress || [],
      wellnessProgress: currentGraphData.wellness_progress || [],
    };
  }, [currentGraphData]);

  // Convert weight from kg to lbs
  const kgToLbs = (kg) => {
    if (!kg) return 0;
    return (kg * 2.20462).toFixed(1);
  };

  // Convert chart data based on selected unit
  const convertChartData = (data) => {
    if (!data || selectedUnit !== "lbs") return data;

    return data.map((item) => {
      if (activeTab === "weight" && item.current_weight) {
        return {
          ...item,
          current_weight: parseFloat(kgToLbs(item.current_weight)),
        };
      }
      return item;
    });
  };

  // Get current chart data based on active tab
  const getCurrentChartData = () => {
    let data;
    switch (activeTab) {
      case "weight":
        data = processedData.weightProgress;
        break;
      case "bodyFat":
        data = processedData.bodyFatProgress;
        break;
      case "waist":
        data = processedData.waistProgress;
        break;
      default:
        data = [];
    }

    return convertChartData(data);
  };

  // Get data key based on active tab
  const getDataKey = () => {
    switch (activeTab) {
      case "weight":
        return "current_weight";
      case "bodyFat":
        return "current_body_fat_percentage";
      case "waist":
        return "current_waist_circumference";
      default:
        return "";
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const startingWeight = detailed_metrics?.starting_weight || 0;
    const goalWeight = detailed_metrics?.goal_weight || 0;
    const progressDifference = detailed_metrics?.progress;

    // Convert weights if needed
    const displayStartingWeight =
      selectedUnit === "lbs"
        ? parseFloat(kgToLbs(startingWeight))
        : startingWeight;

    const displayGoalWeight =
      selectedUnit === "lbs" ? parseFloat(kgToLbs(goalWeight)) : goalWeight;

    // Find the latest weight in the current graph data for progress calculation
    const currentWeight =
      processedData.weightProgress.length > 0
        ? processedData.weightProgress[processedData.weightProgress.length - 1]
            .current_weight
        : displayStartingWeight;

    return {
      startingWeight: displayStartingWeight,
      goalWeight: displayGoalWeight,
      progress: progressDifference,
    };
  };

  const stats = calculateStats();

  // Format value based on tab
  const formatValue = (value) => {
    if (value === null || value === undefined) return "0";

    switch (activeTab) {
      case "weight":
        return `${parseFloat(value).toFixed(0)} ${selectedUnit}`;
      case "bodyFat":
        return `${parseFloat(value).toFixed(0)}%`;
      case "waist":
        return `${parseFloat(value).toFixed(0)} cm`;
      default:
        return parseFloat(value).toFixed(0);
    }
  };

  // Get chart label
  const getChartLabel = () => {
    switch (activeTab) {
      case "weight":
        return `Weight (${selectedUnit})`;
      case "bodyFat":
        return "Body Fat (%)";
      case "waist":
        return "Waist Circumference (cm)";
      default:
        return "";
    }
  };

  const currentChartData = getCurrentChartData();
  const dataKey = getDataKey();
  const hasData = currentChartData && currentChartData.length > 0;

  // Calculate chart width based on number of data points
  const calculateChartWidth = () => {
    const dataPoints = currentChartData.length;
    const minDataPointsForScroll = 6; // Show scroll when more than 6 data points

    if (dataPoints <= minDataPointsForScroll) {
      return "100%";
    }

    // Calculate width based on data points (120px per data point for comfortable spacing)
    const pointsWidth = dataPoints * 120;
    return `${pointsWidth}px`;
  };

  // Check if we need scroll
  const needsScroll = currentChartData?.length > 6;

  return (
    <div className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] p-6 rounded-3xl text-white min-h-[500px]">
      {/* Header */}
      <div className="flex flex-col items-start gap-2 sm:flex-row justify-between sm:items-center mb-3">
        <h2 className="text-[16px] font-[500]">
          Your Physical Transformation Overview
        </h2>
        <div className="flex items-center gap-3">
          {weekRanges.length > 0 && (
            <>
              <select
                className="bg-white rounded-lg px-3 py-3 text-[12px] text-[#000] outline-none cursor-pointer"
                value={selectedUnit}
                onChange={(e) => updateUnit(e.target.value)}
              >
                <option value="lbs">Lbs</option>
                <option value="kg">Kg</option>
              </select>

              <select
                className="bg-white rounded-lg px-3 py-3 text-[12px] text-[#000] outline-none cursor-pointer"
                value={selectedWeekRange}
                onChange={handleWeekRangeChange}
                disabled={isLoading}
              >
                {weekRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      <div className="w-full">
        {/* Chart Section */}
        <div className="bg-white rounded-2xl p-2 md:p-6">
          {/* Chart Tabs */}
          <div className="flex flex-col [@media(min-width:1350px)]:flex-row md:items-center gap-2 justify-between mb-2 [@media(min-width:1350px)]:mb-0">
            <div className="flex flex-col md:flex-row bg-white rounded-full p-1 md:border md:border-[#E9E9E9] gap-0 md:gap-1 mb-2 xl:my-3 gap-2  ">
              <button
                onClick={() => setActiveTab("weight")}
                title="Weight Trend"
                className={`${
                  activeTab === "weight"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium transition-colors border border-[#E9E9E9] md:border-none`}
              >
                Weight Trend
              </button>
              <button
                onClick={() => setActiveTab("bodyFat")}
                title="Body Fat %"
                className={`${
                  activeTab === "bodyFat"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium transition-colors border border-[#E9E9E9] md:border-none`}
              >
                Body Fat %
              </button>
              <button
                onClick={() => setActiveTab("waist")}
                title="Waist Circumference"
                className={`${
                  activeTab === "waist"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium transition-colors border border-[#E9E9E9] md:border-none`}
              >
                Waist Circumference
              </button>
            </div>

            {/* Stats - Only show for weight tab */}
            {detailed_metrics && (
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <div className="text-[#E58B00] text-[12px] bg-[#FBEFDB] rounded-full px-2 py-1 flex items-center gap-2 cursor-default">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    📊
                  </p>
                  Starting Weight: {formatValue(stats.startingWeight)}
                </div>

                <div className="text-[#4CA842] text-[12px] bg-[#D7FFD2] rounded-full px-2 py-1 flex items-center gap-2 cursor-default">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    📈
                  </p>
                  Progress: {stats.progress <= 0 ? "-" : "+"}
                  {Math.abs(stats.progress).toFixed(1)} {selectedUnit}
                </div>

                <div className="text-[#E50067] text-[12px] bg-[#FBDBEB] rounded-full px-2 py-1 flex items-center gap-2 cursor-default">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    🎯
                  </p>
                  Goals: {formatValue(stats.goalWeight)}
                </div>
              </div>
            )}
          </div>
          {/* Chart */}
          <div className="relative">
            {isLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : hasData ? (
              <>
                {/* Scrollable container for all screens when data is large */}
                <div
                  className={`overflow-x-auto overflow-y-visible ${
                    needsScroll
                      ? "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-2"
                      : ""
                  }`}
                  style={{
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: calculateChartWidth(),
                      minWidth: "100%",
                    }}
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={currentChartData}
                        margin={{ top: 15, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#D6D6D6"
                        />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: "#6B7280",
                            fontSize: 12,
                          }}
                          padding={{ left: 15, right: 15 }}
                          interval={needsScroll ? "preserveStartEnd" : 0}
                          // padding={{ left: 10, right: 10 }}
                          tickFormatter={(value) => {
                            if (window.innerWidth < 768) {
                              const match = value.match(/Week\s*(\d+)/i);
                              return match ? `W${match[1]}` : value;
                            }
                            return value;
                          }}
                        />

                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: "#6B7280",
                            fontSize: 12,
                          }}
                          tickFormatter={(value) => formatValue(value)}
                        />
                        <Tooltip
                          formatter={(value) => [
                            formatValue(value),
                            getChartLabel(),
                          ]}
                          labelFormatter={(label) => `${label}`}
                        />
                        <Line
                          type="monotone"
                          dataKey={dataKey}
                          stroke="#3B82F6"
                          strokeWidth={3}
                          dot={{ r: 5, fill: "#3B82F6" }}
                          activeDot={{ r: 8, fill: "#3B82F6" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Scroll hint for users */}
                {needsScroll && (
                  <div className="text-center mt-2">
                    <p className="text-xs text-gray-500">
                      ← Scroll horizontally to see more data →
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <p className="text-[14px] font-[500] mb-1">
                    No Data Available
                  </p>
                  <p className="text-[12px] text-gray-400">
                    Your Physical Transformation Overview will appear here once
                    you complete your weekly check-in. If this week's check-in
                    isn't open yet, please check back soon!
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekGraph;
